import type { IntegrationLocationOption } from "../../../../shared/types";

export interface LocationNode {
  id: string;
  label: string;
  path: string;
  option?: IntegrationLocationOption;
  children: LocationNode[];
}
export interface LocationRow {
  node: LocationNode;
  ancestors: LocationNode[];
  depth: number;
  ancestorIndices: number[];
  index: number;
  /** Exclusive end of this branch in the currently visible rows. */
  end: number;
}
export const LOCATION_ROW_HEIGHT = 52;
export const LOCATION_STICKY_HEIGHT = 40;
const parts = (text?: string) =>
  (text ?? "")
    .split(/\s*[>›]\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
export const normalizeLocationQuery = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .replace(/[>›]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Procore puts the full path in `name` and the leaf in `path`; ACC/files do
 * the reverse. Parent IDs are authoritative when supplied. Missing ancestors
 * become navigable groups, never fabricated selectable provider locations. */
export function buildLocationTree(
  options: IntegrationLocationOption[],
): LocationNode[] {
  const byId = new Map(options.map((option) => [option.remoteId, option]));
  const resolved = new Map<string, string[]>();
  function resolve(
    option: IntegrationLocationOption,
    seen = new Set<string>(),
  ): string[] {
    const cached = resolved.get(option.remoteId);
    if (cached) return cached;
    const name = parts(option.name),
      path = parts(option.path);
    const own = name.length >= path.length ? name : path;
    const leaf = own.at(-1) || option.name;
    if (seen.has(option.remoteId)) return [leaf];
    seen.add(option.remoteId);
    const parent = option.parentRemoteId && byId.get(option.parentRemoteId);
    const result =
      parent && !seen.has(parent.remoteId)
        ? [...resolve(parent, seen), leaf]
        : own;
    resolved.set(option.remoteId, result.length ? result : [leaf]);
    return result;
  }
  const owners = new Map<string, IntegrationLocationOption[]>();
  for (const option of byId.values()) {
    const key = JSON.stringify(resolve(option));
    owners.set(key, [...(owners.get(key) ?? []), option]);
  }
  const nodes = new Map<string, LocationNode>();
  const roots: LocationNode[] = [];
  function ensure(
    chain: string[],
    actual?: IntegrationLocationOption,
  ): LocationNode {
    const key = JSON.stringify(chain);
    const candidates = owners.get(key) ?? [];
    const option =
      actual ?? (candidates.length === 1 ? candidates[0] : undefined);
    const id = option ? `id:${option.remoteId}` : `path:${key}`;
    const existing = nodes.get(id);
    if (existing) return existing;
    const node: LocationNode = {
      id,
      label: chain.at(-1)!,
      path: chain.join(" > "),
      option,
      children: [],
    };
    nodes.set(id, node);
    if (chain.length > 1) {
      const parent = option?.parentRemoteId && byId.get(option.parentRemoteId);
      // Only follow parent IDs whose resolved chain is actually our prefix;
      // corrupt cycles cannot recurse forever or create a cyclic render tree.
      const prefix = chain.slice(0, -1);
      const parentOption =
        parent && JSON.stringify(resolve(parent)) === JSON.stringify(prefix)
          ? parent
          : undefined;
      ensure(prefix, parentOption).children.push(node);
    } else roots.push(node);
    return node;
  }
  for (const option of byId.values()) ensure(resolve(option), option);
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });
  function sort(list: LocationNode[]) {
    list.sort(
      (a, b) => collator.compare(a.label, b.label) || a.id.localeCompare(b.id),
    );
    for (const node of list) sort(node.children);
  }
  sort(roots);
  return roots;
}

/** Search preserves the tree and its ancestors. A parent match includes its
 * descendants; matching several words can span different path segments. */
export function visibleLocationRows(
  tree: LocationNode[],
  query: string,
  collapsed: ReadonlySet<string>,
): LocationRow[] {
  const terms = normalizeLocationQuery(query).split(/\s+/).filter(Boolean);
  const matches = new Set<string>();
  function mark(node: LocationNode): boolean {
    const self = terms.every((term) =>
      normalizeLocationQuery(node.path).includes(term),
    );
    let child = false;
    for (const n of node.children) child = mark(n) || child;
    if (self || child) matches.add(node.id);
    return self || child;
  }
  for (const node of tree) mark(node);
  const rows: LocationRow[] = [];
  function visit(
    node: LocationNode,
    ancestors: LocationNode[],
    ancestorIndices: number[],
  ) {
    if (!matches.has(node.id)) return;
    const row = {
      node,
      ancestors,
      ancestorIndices,
      depth: ancestors.length,
      index: rows.length,
      end: 0,
    };
    rows.push(row);
    if (!collapsed.has(node.id))
      for (const child of node.children)
        visit(child, [...ancestors, node], [...ancestorIndices, row.index]);
    row.end = rows.length;
  }
  for (const node of tree) visit(node, [], []);
  return rows;
}

/** Each ancestor pins only after its original row crosses its slot. At a
 * sibling boundary, deeper ancestors leave before the next branch arrives. */
export function stickyLocationRows(
  rows: LocationRow[],
  scrollTop: number,
  maxSlots = Infinity,
): LocationRow[] {
  const active: LocationRow[] = [];
  for (let depth = 0; depth < 64; depth++) {
    const edge =
      scrollTop +
      Math.min(depth, Math.max(0, maxSlots - 1)) * LOCATION_STICKY_HEIGHT;
    const row =
      rows[Math.min(rows.length - 1, Math.floor(edge / LOCATION_ROW_HEIGHT))];
    if (!row) break;
    const candidate =
      row.ancestorIndices[depth] !== undefined
        ? rows[row.ancestorIndices[depth]!]
        : row.depth === depth
          ? row
          : undefined;
    if (
      !candidate ||
      candidate.end === candidate.index + 1 ||
      candidate.index * LOCATION_ROW_HEIGHT >= edge ||
      candidate.ancestors.some(
        (ancestor, i) => active[i]?.node.id !== ancestor.id,
      )
    )
      break;
    active.push(candidate);
  }
  return active;
}

/** Older detail editors share the catalog loader but still display a flat
 * typeahead. Keep those choices filtered when the review tree loads all pages. */
export function filterLocationOptions(
  options: IntegrationLocationOption[],
  query: string,
): IntegrationLocationOption[] {
  const terms = normalizeLocationQuery(query).split(" ").filter(Boolean);
  return options.filter((option) => {
    const text = normalizeLocationQuery(`${option.name} ${option.path ?? ""}`);
    return terms.every((term) => text.includes(term));
  });
}
